// PortalStaffPortal.jsx — shared portal pages (dashboard/profile/attendance/leaves/salary)
// for the two new staff roles: Librarian and Hostel Member. Both roles get the exact
// same self-service plumbing as Coordinator/Finance (see CoordinatorPages.jsx /
// CoordOwnLeave.jsx, which this is modeled on) — this file is a factory so the two
// roles don't need two near-identical copies of ~600 lines each.
import PasswordInput from '../components/PasswordInput';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon, XMarkIcon, CheckCircleIcon, XCircleIcon, ClockIcon, CalendarDaysIcon,
} from '@heroicons/react/24/outline';

const sharedStyle = (accent) => `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .pp * { box-sizing:border-box; }
  .pp { font-family:'Sora',sans-serif; color:#1a1d2e; animation:ppFade .4s ease both; }
  @keyframes ppFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .pp-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .pp-title  { font-size:1.3rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .pp-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:8px 14px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:14px; height:14px; stroke-width:2; }
  .btn-accent { background:${accent}; color:white; }
  .btn-accent:hover { filter:brightness(0.92); box-shadow:0 4px 14px ${accent}55; }
  .btn-ghost  { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-red    { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover { background:#ef4444; color:white; }
  .btn-sm { padding:5px 10px; font-size:.73rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .row-item { display:flex; align-items:center; justify-content:space-between; padding:12px 18px; border-bottom:1px solid #f5f6fb; gap:10px; flex-wrap:wrap; }
  .row-item:last-child { border-bottom:none; }
  .row-item:hover { background:#fafbff; }
  .row-title { font-size:.82rem; font-weight:600; color:#1a1d2e; }
  .row-sub   { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .pill { font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; display:inline-flex; }
  .pill-pending  { background:#fffbeb; color:#f59e0b; }
  .pill-approved { background:#ecfdf5; color:#10c98e; }
  .pp-empty   { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .pp-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:${accent}; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .stat-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:.9rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.1rem; border:1px solid #e8ebf4; }
  .s-icon { width:32px; height:32px; border-radius:9px; display:flex; align-items:center; justify-content:center; margin-bottom:.6rem; }
  .s-icon svg { width:15px; height:15px; stroke-width:1.8; }
  .s-icon.amber{background:#fffbeb;color:#f59e0b} .s-icon.green{background:#ecfdf5;color:#10c98e}
  .s-icon.blue{background:#eef2ff;color:#3b6ef8} .s-icon.red{background:#fef2f2;color:#ef4444}
  .s-label { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .s-value { font-size:1.4rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; }
  .charts-row { display:grid; grid-template-columns:1fr 1fr; gap:1rem; margin-bottom:1.4rem; }
  .chart-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.4rem; }
  .chart-title { font-size:.86rem; font-weight:600; color:#1a1d2e; margin-bottom:4px; }
  .chart-sub   { font-size:.72rem; color:#9094a8; margin-bottom:1rem; }
  .custom-tooltip { background:white; border:1px solid #e8ebf4; border-radius:10px; padding:8px 12px; box-shadow:0 8px 24px rgba(15,17,35,.12); font-family:'Sora',sans-serif; }
  .ct-label { font-size:.72rem; font-weight:700; color:#0f1123; margin-bottom:4px; }
  .ct-row { display:flex; align-items:center; gap:6px; font-size:.72rem; color:#6b7194; }
  .ct-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
  .chart-empty { padding:2.5rem 0; text-align:center; color:#9094a8; font-size:.8rem; }
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal { background:white; border-radius:20px; padding:26px; width:100%; max-width:480px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head  { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:30px; height:30px; border-radius:8px; border:none; background:#f0f2f7; color:#6b7194; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:20px; flex-wrap:wrap; }
  .form-grid  { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .form-field { display:flex; flex-direction:column; gap:5px; }
  .form-field.span2 { grid-column:1/-1; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-select,.form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-select:focus,.form-textarea:focus { border-color:${accent}; background:white; }
  .form-textarea { resize:vertical; min-height:70px; }
  .checkin-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.4rem; margin-bottom:1.4rem; display:flex; align-items:center; justify-content:space-between; gap:1rem; flex-wrap:wrap; }
  .checkin-info  { display:flex; align-items:center; gap:12px; flex:1; min-width:0; }
  .checkin-dot   { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
  .checkin-dot.in  { background:#10c98e; box-shadow:0 0 0 3px rgba(16,201,142,.2); animation:pulse 2s infinite; }
  .checkin-dot.out { background:#f59e0b; box-shadow:0 0 0 3px rgba(245,158,11,.2); }
  .checkin-dot.off { background:#d1d5db; }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
  .checkin-label { font-size:.78rem; font-weight:600; color:#1a1d2e; }
  .checkin-sub   { font-size:.7rem; color:#9094a8; margin-top:1px; }
  .checkin-times { display:flex; gap:1.5rem; flex-shrink:0; flex-wrap:wrap; }
  .checkin-time-item  { text-align:center; }
  .checkin-time-label { font-size:.65rem; color:#9094a8; font-weight:600; text-transform:uppercase; letter-spacing:.05em; }
  .checkin-time-val   { font-size:.9rem; font-weight:700; font-family:'JetBrains Mono',monospace; color:#1a1d2e; margin-top:2px; }
  .checkin-btn { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .checkin-btn svg { width:15px; height:15px; stroke-width:2; }
  .checkin-btn.green { background:#10c98e; color:white; }
  .checkin-btn.green:hover { background:#0db87e; }
  .checkin-btn.red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .checkin-btn.ghost { background:#f0f2f7; color:#9094a8; }
  .checkin-btn:disabled { opacity:.5; cursor:not-allowed; }
  @media (max-width:768px) { .checkin-card { flex-direction:column; align-items:flex-start; gap:.8rem; } .stat-grid{grid-template-columns:repeat(2,1fr);} .charts-row{grid-template-columns:1fr;} .form-grid{grid-template-columns:1fr;} .form-field.span2{grid-column:1;} .modal-btns{justify-content:stretch;} .modal-btns .btn{flex:1;justify-content:center;} }
`;

const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit' }) : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { day:'numeric', month:'short', year:'numeric' }) : '—';
const dayCount = (s, e) => Math.round((new Date(e)-new Date(s))/86400000)+1;
const typeLabel   = { sick:'Sick', casual:'Casual', emergency:'Emergency', maternity:'Maternity', annual:'Annual', unpaid:'Unpaid', other:'Other' };
const statusLabel = { pending:'Pending', 'approved-paid':'Approved (Paid)', 'approved-unpaid':'Approved (Unpaid)', rejected:'Rejected' };

const CheckinCard = ({ today, actioning, onCheckIn, onCheckOut }) => {
  if (!today?.checkinEnabled) return null;
  const rec = today.record;
  const hasIn = !!rec?.checkIn, hasOut = !!rec?.checkOut;
  return (
    <div className="checkin-card">
      <div className="checkin-info">
        <div className={`checkin-dot ${hasOut?'out':hasIn?'in':'off'}`}/>
        <div>
          <div className="checkin-label">{hasOut?'Checked Out':hasIn?'Currently Checked In':'Not Checked In'}</div>
          <div className="checkin-sub">{rec?.lateMinutes>0?`${rec.lateMinutes}m late today`:'Right on time'}</div>
        </div>
      </div>
      {rec && (
        <div className="checkin-times">
          <div className="checkin-time-item"><div className="checkin-time-label">Check In</div><div className="checkin-time-val">{fmtTime(rec?.checkIn)}</div></div>
          {hasOut&&<div className="checkin-time-item"><div className="checkin-time-label">Check Out</div><div className="checkin-time-val">{fmtTime(rec?.checkOut)}</div></div>}
          {hasOut&&rec?.workHours&&<div className="checkin-time-item"><div className="checkin-time-label">Hours</div><div className="checkin-time-val">{Number(rec.workHours||0).toFixed(1)}h</div></div>}
        </div>
      )}
      <div>
        {!hasIn&&<button className="checkin-btn green" onClick={onCheckIn} disabled={actioning}><CheckCircleIcon/>{actioning?'Please wait…':'Check In'}</button>}
        {hasIn&&!hasOut&&<button className="checkin-btn red" onClick={onCheckOut} disabled={actioning}><XCircleIcon/>{actioning?'Please wait…':'Check Out'}</button>}
        {hasOut&&<button className="checkin-btn ghost" disabled>Done for today</button>}
      </div>
    </div>
  );
};

export const createPortalStaffPages = ({ apiBase, roleLabel, accent, quickLinks = [], DashboardExtra }) => {
  const css = sharedStyle(accent);

  const Dashboard = () => {
    const [today, setToday] = useState(null);
    const [actioning, setActioning] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchToday = async () => {
      try { const { data } = await api.get(`${apiBase}/own-attendance/today`); setToday(data); } catch { /* silent */ }
      finally { setLoading(false); }
    };
    useEffect(() => { fetchToday(); }, []);

    const handleCheckIn = async () => {
      try { setActioning(true); await api.post(`${apiBase}/own-attendance/checkin`); toast.success('Checked in!'); fetchToday(); }
      catch (err) { toast.error(err.response?.data?.message || 'Check-in failed'); }
      finally { setActioning(false); }
    };
    const handleCheckOut = async () => {
      try { setActioning(true); await api.post(`${apiBase}/own-attendance/checkout`); toast.success('Checked out!'); fetchToday(); }
      catch (err) { toast.error(err.response?.data?.message || 'Check-out failed'); }
      finally { setActioning(false); }
    };

    return (
      <div className="pp"><style>{css}</style>
        <div className="pp-header">
          <div><div className="pp-title">Dashboard</div><div className="pp-sub">Welcome to the {roleLabel} Portal</div></div>
        </div>
        {loading ? <div className="pp-loading"><div className="spinner"/>Loading…</div> : (
          <CheckinCard today={today} actioning={actioning} onCheckIn={handleCheckIn} onCheckOut={handleCheckOut} />
        )}
        {DashboardExtra && <DashboardExtra apiBase={apiBase} accent={accent} />}
        <div className="panel">
          <div className="panel-head"><div className="panel-title">Quick Links</div></div>
          <div style={{padding:'18px 20px',display:'flex',gap:10,flexWrap:'wrap'}}>
            {quickLinks.map(l => (
              <button key={l.href} className="btn btn-ghost" onClick={()=>window.location.href=l.href}>{l.label}</button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const Profile = () => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ name:'' });
    const [pwForm, setPwForm] = useState({ currentPassword:'', newPassword:'', confirm:'' });
    useEffect(() => {
      api.get(`${apiBase}/profile`).then(r=>{ setProfile(r.data); setForm({ name: r.data.name||'' }); }).catch(()=>{}).finally(()=>setLoading(false));
    }, []);
    const save = async () => { try{ setSaving(true); await api.put(`${apiBase}/profile`,form); toast.success('Updated'); }catch{ toast.error('Failed'); }finally{ setSaving(false); } };
    const changePw = async () => {
      if (pwForm.newPassword !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
      try{ await api.put(`${apiBase}/profile/password`,pwForm); toast.success('Password changed'); setPwForm({currentPassword:'',newPassword:'',confirm:''}); }
      catch(e){ toast.error(e.response?.data?.message||'Failed'); }
    };
    if (loading) return <div className="pp"><style>{css}</style><div className="pp-loading"><div className="spinner"/>Loading…</div></div>;
    const ini = (n='') => n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';
    return (
      <div className="pp"><style>{css}</style>
        <div className="pp-header"><div><div className="pp-title">My Profile</div></div></div>
        <div style={{background:`linear-gradient(135deg,${accent},#1a1d2e)`,borderRadius:16,padding:'1.4rem 1.6rem',color:'white',marginBottom:'1rem',display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
          <div style={{width:56,height:56,borderRadius:14,background:'rgba(255,255,255,.2)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.1rem',fontWeight:700,flexShrink:0}}>{ini(profile?.name)}</div>
          <div><div style={{fontSize:'1.1rem',fontWeight:700}}>{profile?.name}</div><div style={{fontSize:'.76rem',opacity:.8}}>{profile?.email}</div>
          <div style={{fontSize:'.72rem',opacity:.7,marginTop:4}}>
            {profile?.designation ? `${profile.designation} · ` : ''}Employee ID: {profile?.employeeId || '—'} · Mobile: {profile?.mobile || '—'}
          </div></div>
        </div>
        <div className="panel" style={{marginBottom:'1rem'}}>
          <div className="panel-head"><div className="panel-title">Update Name</div></div>
          <div style={{padding:'16px 18px'}}>
            <input type="text" value={form.name} onChange={e=>setForm({name:e.target.value})} className="form-input" style={{maxWidth:360,marginBottom:12}} />
            <button className="btn btn-accent" onClick={save} disabled={saving}>{saving?'Saving…':'Update'}</button>
          </div>
        </div>
        <div className="panel">
          <div className="panel-head"><div className="panel-title">Change Password</div></div>
          <div style={{padding:'16px 18px',display:'flex',flexDirection:'column',gap:10,maxWidth:400}}>
            {[['Current Password','currentPassword'],['New Password','newPassword'],['Confirm','confirm']].map(([lbl,key])=>(
              <div key={key}><label className="form-label" style={{display:'block',marginBottom:4}}>{lbl}</label>
              <PasswordInput className="form-input" value={pwForm[key]} onChange={e=>setPwForm({...pwForm,[key]:e.target.value})} /></div>
            ))}
            <button className="btn btn-accent" style={{alignSelf:'flex-start'}} onClick={changePw}>Change Password</button>
          </div>
        </div>
      </div>
    );
  };

  const MyAttendance = () => {
    const [records, setRecords] = useState([]);
    const [summary, setSummary] = useState(null);
    const [month, setMonth] = useState(() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
    const [loading, setLoading] = useState(true);
    useEffect(() => {
      setLoading(true);
      api.get(`${apiBase}/own-attendance?month=${month}`)
        .then(r => { setRecords(r.data.records||[]); setSummary(r.data.summary||null); })
        .catch(() => toast.error('Failed to load attendance'))
        .finally(() => setLoading(false));
    }, [month]);
    return (
      <div className="pp"><style>{css}</style>
        <div className="pp-header">
          <div><div className="pp-title">My Attendance</div><div className="pp-sub">Your attendance history</div></div>
          <input type="month" value={month} onChange={e=>setMonth(e.target.value)} className="form-input" style={{width:'auto'}} />
        </div>
        {summary && (
          <div className="stat-grid">
            <div className="stat-card"><div className="s-icon green"><CheckCircleIcon/></div><div className="s-label">Present</div><div className="s-value">{summary.present}</div></div>
            <div className="stat-card"><div className="s-icon red"><XCircleIcon/></div><div className="s-label">Absent</div><div className="s-value">{summary.absent}</div></div>
            <div className="stat-card"><div className="s-icon amber"><ClockIcon/></div><div className="s-label">On Leave</div><div className="s-value">{summary.onLeave}</div></div>
            <div className="stat-card"><div className="s-icon blue"><CalendarDaysIcon/></div><div className="s-label">Late Days</div><div className="s-value">{summary.lateCount}</div></div>
          </div>
        )}
        <div className="panel">
          <div className="panel-head"><div className="panel-title">Records ({records.length})</div></div>
          {loading ? <div className="pp-loading"><div className="spinner"/>Loading…</div>
          : records.length===0 ? <div className="pp-empty">No attendance records for this month.</div>
          : records.map(r => (
            <div key={r._id} className="row-item">
              <div><div className="row-title">{fmtDate(r.date)}</div><div className="row-sub">In: {fmtTime(r.checkIn)} · Out: {fmtTime(r.checkOut)}</div></div>
              <span className={`pill ${r.status==='present'?'pill-approved':'pill-pending'}`}>{r.status}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const MyLeaves = () => {
    const [leaves,    setLeaves]    = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [applyOpen, setApplyOpen] = useState(false);
    const [submitting,setSubmitting]= useState(false);
    const [form, setForm] = useState({ startDate:'', endDate:'', type:'sick', reason:'' });
    const [quota, setQuota] = useState(null);

    const fetchLeaves = async () => {
      try { setLoading(true); const { data } = await api.get(`${apiBase}/leaves`); setLeaves(data); }
      catch { toast.error('Failed to load leaves'); } finally { setLoading(false); }
    };
    useEffect(() => { fetchLeaves(); }, []);
    useEffect(() => { api.get(`${apiBase}/leaves/quota`).then(r => setQuota(r.data?.stats||null)).catch(() => {}); }, []);

    const handleApply = async () => {
      if (!form.startDate||!form.endDate) { toast.error('Please select dates'); return; }
      if (!form.reason.trim()) { toast.error('Reason is required'); return; }
      try {
        setSubmitting(true);
        await api.post(`${apiBase}/leaves`, form);
        toast.success('Leave application submitted');
        setApplyOpen(false); setForm({ startDate:'', endDate:'', type:'sick', reason:'' }); fetchLeaves();
      } catch (err) {
        const d = err.response?.data;
        if (d?.quotaExceeded) toast.error(d.message, { duration:5000 });
        else if (d?.offDayBlocked) toast.error('Cannot apply leave on off-days or holidays', { duration:4000 });
        else toast.error(d?.message||'Failed to apply');
      } finally { setSubmitting(false); }
    };
    const handleCancel = async (id) => {
      if (!window.confirm('Cancel this leave application?')) return;
      try { await api.delete(`${apiBase}/leaves/${id}`); toast.success('Leave cancelled'); fetchLeaves(); }
      catch (err) { toast.error(err.response?.data?.message||'Failed'); }
    };

    const pending        = leaves.filter(l => l.status==='pending').length;
    const approvedPaid   = leaves.filter(l => l.status==='approved-paid').length;
    const approvedUnpaid = leaves.filter(l => l.status==='approved-unpaid').length;
    const rejected       = leaves.filter(l => l.status==='rejected').length;

    return (
      <div className="pp"><style>{css}</style>
        <div className="pp-header">
          <div><div className="pp-title">My Leaves</div><div className="pp-sub">Apply for leave and track application status</div></div>
          <button className="btn btn-accent" onClick={() => setApplyOpen(true)}><PlusIcon />Apply for Leave</button>
        </div>
        <div className="stat-grid">
          <div className="stat-card"><div className="s-icon amber"><ClockIcon /></div><div className="s-label">Pending</div><div className="s-value">{pending}</div></div>
          <div className="stat-card"><div className="s-icon green"><CheckCircleIcon /></div><div className="s-label">Approved Paid</div><div className="s-value">{approvedPaid}</div></div>
          <div className="stat-card"><div className="s-icon blue"><CheckCircleIcon /></div><div className="s-label">Approved Unpaid</div><div className="s-value">{approvedUnpaid}</div></div>
          <div className="stat-card"><div className="s-icon red"><XCircleIcon /></div><div className="s-label">Rejected</div><div className="s-value">{rejected}</div></div>
        </div>
        <div className="panel">
          {loading ? <div className="pp-loading"><div className="spinner"/>Loading…</div>
          : leaves.length===0 ? <div className="pp-empty">No leave applications yet. Click "Apply for Leave" to submit your first one.</div>
          : leaves.map(l => (
            <div key={l._id} className="row-item">
              <div>
                <div className="row-title">{typeLabel[l.type]||l.type} · {fmtDate(l.startDate)}{l.startDate!==l.endDate&&` → ${fmtDate(l.endDate)}`} ({dayCount(l.startDate,l.endDate)}d)</div>
                <div className="row-sub">{l.reason || '—'}{l.adminNote ? ` · Note: "${l.adminNote}"` : ''}</div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span className={`pill ${l.status==='pending'?'pill-pending':'pill-approved'}`}>{statusLabel[l.status]}</span>
                {l.status==='pending'&&<button className="btn btn-red btn-sm" onClick={()=>handleCancel(l._id)}>Cancel</button>}
              </div>
            </div>
          ))}
        </div>
        {applyOpen && createPortal(
          <div className="modal-backdrop" onClick={()=>setApplyOpen(false)}>
            <div className="modal" onClick={e=>e.stopPropagation()}>
              <div className="modal-head"><div className="modal-title">Apply for Leave</div><button className="modal-close" onClick={()=>setApplyOpen(false)}><XMarkIcon /></button></div>
              <div className="form-grid">
                <div className="form-field span2">
                  <label className="form-label">Leave Type</label>
                  <select className="form-select" value={form.type} onChange={e=>setForm({...form,type:e.target.value})}>
                    <option value="sick">Sick Leave</option>
                    <option value="casual">Casual Leave</option>
                    <option value="emergency">Emergency Leave</option>
                    <option value="maternity">Maternity Leave</option>
                    <option value="annual">Annual Leave</option>
                    <option value="unpaid">Unpaid Leave</option>
                    <option value="other">Other</option>
                  </select>
                  {quota && (() => {
                    const q = quota.find(x => x.type===form.type);
                    if (!q) return null;
                    const isUnlimited = q.quota==='unlimited'||Number(q.quota)>=999;
                    return (
                      <div style={{marginTop:6,fontSize:'.72rem',color:'#6b7194'}}>
                        {isUnlimited ? 'Unlimited quota' : `${q.remaining} of ${q.quota} days remaining this year`}
                      </div>
                    );
                  })()}
                </div>
                <div className="form-field"><label className="form-label">Start Date</label><input type="date" className="form-input" value={form.startDate} onChange={e=>setForm({...form,startDate:e.target.value})} /></div>
                <div className="form-field"><label className="form-label">End Date</label><input type="date" className="form-input" value={form.endDate} min={form.startDate} onChange={e=>setForm({...form,endDate:e.target.value})} /></div>
                <div className="form-field span2"><label className="form-label">Reason</label><textarea className="form-textarea" required value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})} placeholder="Describe your reason…" /></div>
              </div>
              <div className="modal-btns">
                <button className="btn btn-ghost" onClick={()=>setApplyOpen(false)}>Cancel</button>
                <button className="btn btn-accent" onClick={handleApply} disabled={submitting}>{submitting?'Submitting…':'Submit Application'}</button>
              </div>
            </div>
          </div>, document.body)}
      </div>
    );
  };

  const MySalary = () => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
      api.get(`${apiBase}/salary/history`)
        .then(r => setHistory(Array.isArray(r.data) ? r.data : []))
        .catch(() => toast.error('Failed to load salary'))
        .finally(() => setLoading(false));
    }, []);
    return (
      <div className="pp"><style>{css}</style>
        <div className="pp-header"><div><div className="pp-title">My Salary</div><div className="pp-sub">View only</div></div></div>
        <div className="panel">
          <div className="panel-head"><div className="panel-title">Salary Records ({history.length})</div></div>
          {loading?<div className="pp-loading"><div className="spinner"/>Loading…</div>
          :history.length===0?<div className="pp-empty">No salary records yet.</div>
          :history.map(s=>(
            <div key={s._id} className="row-item">
              <div><div className="row-title">{s.month}</div><div className="row-sub">Present {s.daysPresent}/{s.workingDays} days</div></div>
              <div style={{textAlign:'right'}}>
                <div style={{fontWeight:700,fontFamily:"'JetBrains Mono',monospace",fontSize:'.84rem'}}>Rs. {Number(s.netSalary||0).toLocaleString()}</div>
                <span className={`pill ${s.status==='paid'?'pill-approved':'pill-pending'}`}>{s.status||'unpaid'}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return { Dashboard, Profile, MyAttendance, MyLeaves, MySalary };
};
